import { memo } from "react";

import { Loader } from "@/components/loader/Loader.tsx";
import { Button } from "@/components/ui/button.tsx";
import type { UserTask } from "@/types/userTasks.ts";
import { UserTaskRow } from "./UserTaskRow.tsx";

/**
 * A thread's tasks on the user's list, as the thread's task view and the
 * composer show them: the same rows, and the same actions, as the Tasks tab.
 * Memoized, as the per-thread list is: most renders here come from the
 * composer, not from the tasks.
 */
export const ThreadUserTaskList = memo(function ThreadUserTaskList({
  tasks,
  isLoading,
  isError,
  onRetry,
  emptyMessage,
}: {
  tasks: UserTask[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  emptyMessage?: string;
}) {
  if (isLoading) return <Loader className="py-4" />;
  if (isError) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        Couldn't load tasks.
        <Button
          variant="link"
          size="fit"
          className="h-auto p-0 text-xs"
          onClick={onRetry}
          data-statsig-name="thread-tasks-retry"
        >
          Try again
        </Button>
      </div>
    );
  }
  if (tasks.length === 0) {
    return emptyMessage ? <p className="text-xs text-muted-foreground">{emptyMessage}</p> : null;
  }
  return (
    <ul className="flex flex-col gap-2">
      {tasks.map((task) => (
        <UserTaskRow key={task.id} task={task} />
      ))}
    </ul>
  );
});
